<template>
  <div :class="'image-slot' + (dragOver ? ' image-slot-dragover' : '')" @dragover.prevent="dragOver = !disabled" @dragleave.prevent="dragOver = false" @drop.prevent="onDrop">
    <span class="thumbnail" v-if="hasImage">
      <img :id="imageElementId" class="img-responsive" :src="src" />
      <button :id="removeButtonId" class="removeimage btn btn-danger" type="button" v-on:click="$emit('remove')"><i class="fa fa-fw fa-trash"></i></button>
    </span>
    <p v-else-if="emptyText">{{ emptyText }}</p>
    <upload-progress :uploads="uploads" />
    <div class="form-group" v-if="!hasImage && !disabled && !isUploading">
      <label class="btn btn-success image-slot-upload">
        <input type="file" accept="image/*" :id="inputId" @change="onSelect" />
        <i class="fa fa-fw fa-image"></i> {{ _('Send image') }}
      </label>
      <button type="button" class="btn btn-default image-slot-library" v-on:click="$emit('library')"><i class="fa fa-fw fa-images"></i> {{ _('Choose from files') }}</button>
    </div>
    <div class="form-group" v-if="hasImage && showDetails">
      <button type="button" class="btn btn-sm btn-default mainCaptionButton" v-on:click="$emit('details')"><i class="fa fa-fw fa-edit"></i> {{ _('Details') }}</button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { johkuEmbedDisplaySrc } from '~/lib/tiptap/johkuEmbedImage'

// One image of an entity (e.g. the main image). Selected or dropped files are
// emitted as `upload`; the editor queues them with lib/uploadaware.js and this
// component shows their progress from the upload queue.
export default {
  props: {
    fileId: { default: null },
    ext: { type: String, default: '' },
    ownerKey: { type: String, default: null },
    uploadSlot: { type: String, required: true },
    emptyText: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // Templates and receipt components have no captions
    showDetails: { type: Boolean, default: true },
    inputId: { type: String, default: null },
    imageElementId: { type: String, default: null },
    removeButtonId: { type: String, default: null }
  },
  emits: ['upload', 'remove', 'details', 'library'],
  data () {
    return {
      dragOver: false
    }
  },
  computed: {
    ...mapGetters(['baseDomain', 'shopId']),
    hasImage () {
      return !!parseInt(this.fileId, 10)
    },
    src () {
      // Image CDN, so previews don't load the backend
      return johkuEmbedDisplaySrc(this.baseDomain + this.shopId, this.fileId, 400)
    },
    uploads () {
      if (!this.ownerKey) {
        return []
      }
      return this.$store.state.uploads.filter(u => u.owner === this.ownerKey && u.slot === this.uploadSlot && u.status !== 'done')
    },
    // One image per slot, so no new upload while one is on its way
    isUploading () {
      return this.uploads.some(u => ['queued', 'fetching', 'uploading', 'processing'].includes(u.status))
    }
  },
  methods: {
    onSelect (e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (files.length) {
        this.$emit('upload', files.slice(0, 1))
      }
    },
    onDrop (e) {
      this.dragOver = false
      if (this.disabled || this.hasImage || this.isUploading) {
        return
      }
      const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []).filter(f => f.type.startsWith('image/'))
      if (files.length) {
        this.$emit('upload', files.slice(0, 1))
      }
    }
  }
}
</script>

<style scoped>
.image-slot-upload input[type=file] {
  display: none;
}
.image-slot-library {
  margin-left: 6px;
}
.image-slot-dragover {
  outline: 2px dashed #5cb85c;
  outline-offset: 4px;
}
</style>
